/** @flow */
import api from 'common/services/api';
import type { DayOfWeek } from 'common/domain/DayOfWeek';
import type { DateRange } from '../../../domain/reach';
import { Interval } from '../../../domain/reach';
import ReachPerformance from '../domain/ReachPerformance';
import { CentralMarket } from '../../centralMarkets/domain/CentralMarket';

const ERROR_MESSAGE = 'Something wrong happened when retrieving reach.';
const ISO_DATE = 'YYYY-MM-DD';
const ISO_TIME = 'HH:mm:ss.SSS';

class ReachApi {
  buildHeaders(token: string) {
    return { Authorization: 'Bearer ' + token };
  }

  async getReachByInterval(callSign: string, token: string, dateRange: DateRange, interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/interval`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        interval: interval.step,
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getReachByIntervalCompared(callSign: string, token: string, dateRange: DateRange, comparedDateRange: DateRange,
                                   interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/interval/compare`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        comparedFrom: comparedDateRange.from.format(ISO_DATE),
        comparedTo: comparedDateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        interval: interval.step,
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getTotalReach(callSign: string, token: string, dateRange: DateRange, interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getAverageTotalReach(callSign: string, token: string, dateRange: DateRange, interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/average`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getAverageTotalReachCompared(callSign: string, token: string, dateRange: DateRange, comparedDateRange: DateRange,
                                     interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/average/compare`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        comparedFrom: comparedDateRange.from.format(ISO_DATE),
        comparedTo: comparedDateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getTotalReachCompared(callSign: string, token: string, dateRange: DateRange, comparedDateRange: DateRange,
                              interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/compare`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        comparedFrom: comparedDateRange.from.format(ISO_DATE),
        comparedTo: comparedDateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getAverageQuarterHourReach(callSign: string, token: string, dateRange: DateRange, interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/average-by-interval`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        interval: 15,
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getAverageQuarterHourReachCompared(callSign: string, token: string, dateRange: DateRange, comparedDateRange: DateRange,
                                           interval: Interval, days: Array<DayOfWeek>, centralMarket: ?CentralMarket) {
    try {
      const { data } = await api.get(`/reach/${callSign}/average-by-interval/compare`, {
        from: dateRange.from.format(ISO_DATE),
        to: dateRange.to.format(ISO_DATE),
        startTime: interval.from.format(ISO_TIME),
        endTime: interval.to.format(ISO_TIME),
        interval: 15,
        comparedFrom: comparedDateRange.from.format(ISO_DATE),
        comparedTo: comparedDateRange.to.format(ISO_DATE),
        days: days,
        centralMarket: centralMarket && centralMarket.reference,
      }, {
        headers: this.buildHeaders(token),
      });

      return data;
    } catch (e) {
      throw new Error(ERROR_MESSAGE);
    }
  }

  async getReachPerformance(callSign: string, token: string): ReachPerformance {
    const { data } = await api.get(`/reach/${callSign}/performance`, {}, { headers: this.buildHeaders(token) });

    return ReachPerformance.from(data);
  }
}

export default new ReachApi();



// WEBPACK FOOTER //
// ./src/statsradio/modules/metrics/services/reachApi.js